import { useSyncExternalStore } from 'react';

// Browser-tab context only. The server checks that the caller is a Superadmin
// and resolves the role or person on every request that carries it.
const STORAGE_KEY = 'lineside.viewAs';
const listeners = new Set();

const read = () => {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
    return value && typeof value.target === 'string' ? value : null;
  } catch {
    return null;
  }
};

let current = read();

export const getViewAs = () => current;
export const getViewAsTarget = () => current?.target || null;

export const setViewAs = (viewer) => {
  current = viewer || null;
  try {
    if (current) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // The tab keeps the viewer in memory when storage is unavailable.
  }
  listeners.forEach((listener) => listener());
};

export const subscribeViewAs = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const triedListeners = new Set();

export const subscribeTriedAction = (listener) => {
  triedListeners.add(listener);
  return () => triedListeners.delete(listener);
};

/**
 * While viewing as someone the server answers an Action with a dry run. Show
 * what it would do, and fail where the real Action would have failed.
 */
export function takeDryRun(payload, actionName = null) {
  const dryRun = payload?.dry_run;
  if (!dryRun) return payload;
  triedListeners.forEach((listener) => listener({ name: actionName, ...dryRun }));
  if (!dryRun.succeeded) {
    throw Object.assign(new Error(dryRun.message || 'This Action would fail.'), { status: 400, code: 'dry_run' });
  }
  return payload;
}

/** Part of every View runtime cache key: one viewer's apps are never shown as another's. */
export const useViewAsTarget = () => useSyncExternalStore(subscribeViewAs, getViewAsTarget, () => null);

export const useViewAsViewer = () => useSyncExternalStore(subscribeViewAs, getViewAs, () => null);

/** Only the View runtime and Actions honor the viewer; other requests stay the caller's. */
export const viewAsHeaders = (url) => {
  const target = getViewAsTarget();
  return target && /\/api\/ontology-(views|actions)(\/|\?|$)/.test(String(url))
    ? { 'X-LineSide-View-As': target }
    : {};
};
